import { Box, ButtonBase } from "@mui/material";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";
import { focusRingSx } from "../_shared/controlStyles";

const { brand, mercury, mercuryAlpha, red } = colorPalette;

// Label type, tile size and radius. box = Figma's icon box (keeps the layout exact);
// icon = MUI glyph size inside it. MUI glyphs sit in a padded 24px box, so they need to
// be larger than Figma's 12 / 14 / 16px glyphs to look the same.
const SIZES = {
  sm: {
    height: 28,
    padding: 8,
    radius: 8,
    fontSize: 12,
    spacing: 0.4,
    box: 12,
    icon: 18,
  },
  md: {
    height: 40,
    padding: 12,
    radius: 12,
    fontSize: 16,
    spacing: 0.3,
    box: 14,
    icon: 20,
  },
  lg: {
    height: 52,
    padding: 16,
    radius: 12,
    fontSize: 20,
    spacing: 0.2,
    box: 16,
    icon: 24,
  },
};

const DISABLED_TEXT = mercuryAlpha[28];

// Per variant: background and text per state. `border` is drawn inside (box-shadow),
// like Figma's inside stroke, so it never changes the button size.
const VARIANTS = {
  primary: {
    bg: brand[700],
    hoverBg: brand[800],
    pressedBg: brand[900],
    text: colors.white,
    disabledBg: mercury[300],
  },
  secondary: {
    bg: colors.white,
    hoverBg: brand[100],
    pressedBg: brand[200],
    text: brand[700],
    border: brand[700],
    disabledBg: colors.white,
    disabledBorder: mercuryAlpha[8],
  },
  subtle: {
    bg: brand[100],
    hoverBg: brand[200],
    pressedBg: brand[300],
    text: brand[700],
    pressedText: brand[800],
    disabledBg: mercuryAlpha[5],
  },
  ghost: {
    bg: "transparent",
    hoverBg: mercuryAlpha[5],
    pressedBg: mercuryAlpha[8],
    text: mercury[950],
    disabledBg: "transparent",
  },
  danger: {
    bg: red[600],
    hoverBg: red[700],
    pressedBg: red[800],
    text: colors.white,
    disabledBg: mercury[300],
  },
};

/**
 * Button
 *
 * Props:
 * - variant: primary | secondary | subtle | ghost | danger
 * - size: sm (28px) | md (40px) | lg (52px)
 * - startIcon / endIcon: React nodes (filled @mui/icons-material icons)
 * - disabled
 * - children: label
 * - sx: extra styles, merged last
 * - anything else (onClick, type, component, href, ...) goes to the underlying button
 */
const Button = ({
  variant = "primary",
  size = "md",
  startIcon,
  endIcon,
  disabled = false,
  children,
  sx: sxOverride = {},
  ...rest
}) => {
  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.md;
  const border = (color) => (color ? `inset 0 0 0 1px ${color}` : "none");

  return (
    <ButtonBase
      disableRipple
      disabled={disabled}
      {...rest}
      sx={{
        gap: "4px",
        height: s.height,
        padding: `${s.padding}px`,
        borderRadius: `${s.radius}px`,
        boxSizing: "border-box",
        whiteSpace: "nowrap",
        fontFamily: '"Lexend", sans-serif',
        fontSize: s.fontSize,
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: 1,
        letterSpacing: `${s.spacing}px`,
        backgroundColor: v.bg,
        color: v.text,
        boxShadow: border(v.border),
        "&:hover": { backgroundColor: v.hoverBg },
        "&:active": {
          backgroundColor: v.pressedBg,
          color: v.pressedText || v.text,
        },
        "&.Mui-disabled": {
          backgroundColor: v.disabledBg,
          color: DISABLED_TEXT,
          boxShadow: border(v.disabledBorder),
        },
        "&.Mui-focusVisible": focusRingSx,
        ...sxOverride,
      }}
    >
      {startIcon && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: s.box,
            height: s.box,
            fontSize: s.icon,
            "& svg": {
              fontSize: "inherit",
              width: "1em",
              height: "1em",
              flexShrink: 0,
            },
          }}
        >
          {startIcon}
        </Box>
      )}
      {children}
      {endIcon && (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: s.box,
            height: s.box,
            fontSize: s.icon,
            "& svg": {
              fontSize: "inherit",
              width: "1em",
              height: "1em",
              flexShrink: 0,
            },
          }}
        >
          {endIcon}
        </Box>
      )}
    </ButtonBase>
  );
};

export default Button;
